import { Button } from "@mui/material";
import { FormControlTooltip } from "@verity/components/core";
import i18n from "../../../../../i18n";

export type FlagFilterLevel = "Severe" | "Major" | "Minor";

const LEVELS: { key: FlagFilterLevel; borderColor: string; color: string; labelKey: string }[] = [
	{
		key: "Severe",
		borderColor: "#E54D40",
		color: "#DA3B2D",
		labelKey: "result.components.resultsPage.proctoringEventsTableTSX.severe",
	},
	{
		key: "Major",
		borderColor: "#E0A626",
		color: "#A46200",
		labelKey: "result.components.resultsPage.proctoringEventsTableTSX.major",
	},
	{
		key: "Minor",
		borderColor: "#59B266",
		color: "#5E7831",
		labelKey: "result.components.resultsPage.proctoringEventsTableTSX.minor",
	},
];

type FlagFilterLayoutProps = {
	severeCount?: number;
	majorCount?: number;
	minorCount?: number;
	flagLevelFilter: { [key: string]: boolean };
	onToggle: (level: FlagFilterLevel) => void;
};

/**
 * Quick-filter severity buttons rendered next to the events table's filter button in the new LMS
 * layout — toggling one flips the same `flagLevelFilter` state the filter popover's checkboxes use,
 * so either control stays in sync with the other. Every severity is always shown, including ones with
 * a count of 0, so the row doesn't reflow as counts change. "Info" (flag level 0) is deliberately not
 * one of them — it isn't a severity, and it stays filterable from the popover's checkboxes.
 */
export default function FlagFilterLayout({
	severeCount,
	majorCount,
	minorCount,
	flagLevelFilter,
	onToggle,
}: FlagFilterLayoutProps) {
	const counts: Record<FlagFilterLevel, number> = {
		Severe: severeCount ?? 0,
		Major: majorCount ?? 0,
		Minor: minorCount ?? 0,
	};

	return (
		<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
			{LEVELS.map(({ key, borderColor, color, labelKey }) => {
				const active = !!flagLevelFilter[key];
				const count = counts[key];
				const label = i18n.t(labelKey) as string;

				return (
					<FormControlTooltip
						key={key}
						tooltip={i18n.t("result.components.resultsPage.proctoringEventsTableTSX.flagFilterTooltip", {
							count,
							level: label,
						})}
					>
						<Button
							onClick={() => onToggle(key)}
							aria-pressed={active}
							sx={{
								minWidth: "unset",
								height: "35px",
								padding: "12px",
								borderRadius: "0.7rem",
								fontSize: "15px",
								fontWeight: "400",
								boxSizing: "border-box",
								textTransform: "none",
								gap: "6px",
								border: `1px solid ${borderColor}`,
								backgroundColor: active ? borderColor : "#FFFFFF",
								color: active ? "#FFFFFF" : color,
								"&:hover": {
									backgroundColor: active ? borderColor : "#F9F9F9",
								},
								"&:focus-visible": {
									outline: "2px solid #0b6f85",
									outlineOffset: "2px",
								},
							}}
						>
							<span
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									minWidth: 20,
									height: 20,
									padding: "0 5px",
									boxSizing: "border-box",
									borderRadius: "999px",
									fontSize: 12,
									fontWeight: 500,
									backgroundColor: active ? "#FFFFFF" : borderColor,
									color: active ? color : "#FFFFFF",
									border: active ? `1px solid ${borderColor}` : "none",
								}}
							>
								{count}
							</span>
							{label}
						</Button>
					</FormControlTooltip>
				);
			})}
		</div>
	);
}
